import {
  CameraIcon,
  CameraIconS,
  HomeLogo,
  HomeLogoS,
  PaymentIcon,
  PaymentIconS,
  PayoutIcon,
  PayoutIconS,
  ProductIcon,
  ProductIconS,
  SettingIcon,
  SettingIconS,
  ShopifyS,
  Shopify,
  hostRec,
  hostRecS,
} from 'assets/images';
import { RiBarChartBoxLine, RiBookOpenLine, RiCalendarCheckLine } from '@remixicon/react';
import { CAPABILITIES } from 'utils/capabilities';

/**
 * The store portal menu, as data.
 *
 * Each item names the capability that makes it visible; the sidebar renders
 * whatever the user's role holds, in this order, and `landingPathFor` sends a
 * freshly logged-in user to the first item they can see. There is no per-role
 * branching anywhere: a role with a different menu is a different set of
 * grants in the backend, not a code change here.
 *
 * `capability` may be omitted for items every logged-in user has (Settings).
 * `badge` names the context counter that shows a request dot next to the label.
 * `icon` is a pair of images (idle, active); `remixIcon` is a Remix icon
 * component rendered via SidebarMenuIcon instead.
 */
export const STORE_NAV_ITEMS = [
  {
    path: '/user/welcome',
    key: 'welcome',
    label: 'Home',
    capability: CAPABILITIES.DASHBOARD_VIEW,
    icon: [HomeLogo, HomeLogoS],
  },
  {
    path: '/user/host',
    key: 'host',
    label: 'Real Customer',
    capability: CAPABILITIES.HOSTS_VIEW,
    icon: [PaymentIcon, PaymentIconS],
    badge: 'updatedHosts',
  },
  {
    path: '/user/store-meetings',
    key: 'store-meetings',
    label: 'Video Meetings',
    capability: CAPABILITIES.MEETINGS_VIEW,
    icon: [CameraIcon, CameraIconS],
    badge: 'demoRequests',
  },
  {
    path: '/user/accounting',
    key: 'accounting',
    label: 'Accounting',
    capability: CAPABILITIES.ACCOUNTING_VIEW,
    icon: [PayoutIcon, PayoutIconS],
    badge: 'adminPaymentList',
  },
  {
    path: '/user/badge-analytics',
    key: 'badge-analytics',
    label: 'Live Badge',
    capability: CAPABILITIES.BADGE_VIEW,
    remixIcon: RiBarChartBoxLine,
  },
  {
    // Always shown to roles that can view it, whatever the plan. The page
    // itself prompts to upgrade, which keeps the feature discoverable.
    path: '/user/meeting-takeaways',
    key: 'meeting-takeaways',
    label: 'Meeting Takeaways',
    capability: CAPABILITIES.TAKEAWAYS_VIEW,
    remixIcon: RiBookOpenLine,
  },
  {
    path: '/user/shopifyDetails',
    key: 'shopifyDetails',
    label: 'Shopify Details',
    capability: CAPABILITIES.SHOPIFY_VIEW,
    icon: [Shopify, ShopifyS],
    iconStyle: { marginRight: '3px' },
  },
  {
    path: '/user/flow',
    key: 'flow',
    label: 'Communications',
    capability: CAPABILITIES.COMMUNICATIONS_VIEW,
    icon: [hostRecS, hostRec],
  },
  {
    path: '/user/productsList',
    key: 'productsList',
    label: 'Products',
    capability: CAPABILITIES.PRODUCTS_VIEW,
    icon: [ProductIcon, ProductIconS],
    iconStyle: { marginRight: '3px' },
  },
  { divider: true },
  {
    path: '/user/addRole',
    key: 'addRole',
    label: 'User Roles',
    capability: CAPABILITIES.TEAM_MANAGE,
    icon: [PaymentIcon, PaymentIconS],
  },
  {
    path: '/user/settings',
    key: 'settings',
    label: 'Settings',
    icon: [SettingIcon, SettingIconS],
  },
  {
    path: '/user/subscription',
    key: 'subscription',
    label: 'Subscription',
    capability: CAPABILITIES.SUBSCRIPTION_VIEW,
    remixIcon: RiCalendarCheckLine,
  },
];

/**
 * Items of the store menu the user may see, dividers included only when
 * something visible follows them.
 *
 * @param {Function} can `(capability) => Boolean`
 * @returns {Object[]} visible items
 */
export const visibleStoreNavItems = (can) => {
  const items = STORE_NAV_ITEMS.filter((item) => item.divider || !item.capability || can(item.capability));
  return items.filter((item, index) => {
    if (!item.divider) {
      return true;
    }
    const rest = items.slice(index + 1);
    return index > 0 && rest.some((next) => !next.divider);
  });
};
